import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Modal,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Tooltip,
  TablePagination,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
  Stack,
  Fade,
  alpha,
  useTheme,
  Card,
  FormControlLabel,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import * as actions from '../../redux/actions';
import {
  countProjectGpsPoints,
  getProjectGpsPoints,
} from '../../utils/functions/projectGpsPoints';

const HrCheckinManagementMap = lazy(() =>
  import('./HrCheckinManagementMap'),);

const DEFAULT_CENTER = { lat: 13.7563, lng: 100.5018 };
const DEFAULT_ZOOM = 14;
const PAGE_SIZE = 50;

const getGroupOrder = (projectNumber) => {
  const n = (projectNumber || '').toUpperCase();
  if (n.startsWith('JS_') && !n.startsWith('JSID_')) return 0;
  if (n.startsWith('JSID_')) return 1;
  return 2;
};

const projectSort = (a, b) => {
  const gA = getGroupOrder(a.project_number);
  const gB = getGroupOrder(b.project_number);
  if (gA !== gB) return gA - gB;
  if (gA === 0) return (b.project_number || '').localeCompare(a.project_number || '');
  return (a.project_number || '').localeCompare(b.project_number || '');
};

const MapMarker = () => (
  <Box
    sx={{
      position: 'absolute',
      transform: 'translate(-50%, -100%)',
      color: '#d32f2f',
      fontSize: 36,
    }}
  >
    <LocationOnIcon sx={{ fontSize: 40 }} />
  </Box>
);

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 480 },
  maxWidth: 480,
  maxHeight: '90vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
};

export default function HrCheckinManagement({ title, subtitle }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project);
  const me = useSelector((state) => state.me);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editLocation, setEditLocation] = useState('');
  /** รายการจุดทั้งหมด: แถวแรกบันทึกเป็น gps แถวถัดไปเป็น gps_sites */
  const [editSites, setEditSites] = useState([]);
  const [activeSiteIndex, setActiveSiteIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [message, setMessage] = useState({ type: null, text: '' });
  const [linkModalRow, setLinkModalRow] = useState(null);
  const [bulkGpsSaving, setBulkGpsSaving] = useState(false);

  const total = project?.total ?? 0;
  // best-effort จากแถวที่โหลดอยู่ในหน้านี้ (มี pagination — ไม่ใช่สถานะจริงของทั้งระบบเป๊ะ ๆ)
  const profileProjectsOnPage = projects.filter((p) => p.time_tracking_enabled);
  const allProfileProjectsHaveGps =
    profileProjectsOnPage.length > 0 &&
    profileProjectsOnPage.every((p) => p.checkout_gps_required);

  useEffect(() => {
    setLoading(true);
    dispatch(
      actions.projectAll({ size: PAGE_SIZE, page: page + 1, lite: true }),
    ).then(() => setLoading(false));
  }, [dispatch, page]);

  useEffect(() => {
    const list = project?.rows ?? [];
    setProjects(Array.isArray(list) ? [...list].sort(projectSort) : []);
  }, [project]);

  const handleToggleTimeTracking = async (row, checked) => {
    const confirmMsg = checked
      ? 'เปิดการลงเวลาผ่านหน้า Profile สำหรับโครงการนี้?'
      : 'ปิดการลงเวลาผ่านหน้า Profile สำหรับโครงการนี้?';
    if (!window.confirm(confirmMsg)) return;
    try {
      await dispatch(
        actions.projectPut(row.id || row._id, {
          time_tracking_enabled: checked,
        }),
      );
      
      // Update local state แทนการ reload
      const updatedProjects = projects.map(p => 
        (p.id || p._id) === (row.id || row._id) 
          ? { ...p, time_tracking_enabled: checked } 
          : p
      );
      setProjects(updatedProjects);
      
      setMessage({ type: 'success', text: 'อัปเดตสถานะการลงเวลาแล้ว' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'อัปเดตไม่สำเร็จ' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    }
  };

  const handleToggleTimeTrackingLink = async (row, checked) => {
    const confirmMsg = checked
      ? 'เปิดให้ลงเวลาผ่านลิงก์ Manpower สำหรับโครงการนี้?'
      : 'ปิดการลงเวลาผ่านลิงก์ Manpower (ลงได้เฉพาะ Profile)?';
    if (!window.confirm(confirmMsg)) return;
    try {
      await dispatch(
        actions.projectPut(row.id || row._id, {
          time_tracking_link_enabled: checked,
        }),
      );

      // Update local state แทนการ reload
      const updatedProjects = projects.map(p =>
        (p.id || p._id) === (row.id || row._id)
          ? { ...p, time_tracking_link_enabled: checked }
          : p
      );
      setProjects(updatedProjects);

      setMessage({ type: 'success', text: 'อัปเดตลิงก์ลงเวลาแล้ว' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'อัปเดตไม่สำเร็จ' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    }
  };

  // ลิงก์ Manpower ต่อโครงการ — ใช้ origin ของหน้าที่เปิดอยู่จริง (auto ตามโดเมนเสมอ ไม่ต้องตั้งค่าแยก)
  const getTimeTrackingLink = (row) =>
    `${window.location.origin}/manpower/timestamp/${row.id || row._id}`;

  const handleCopyLink = (row) => {
    navigator.clipboard.writeText(getTimeTrackingLink(row));
    setMessage({ type: 'success', text: 'คัดลอกลิงก์แล้ว' });
    setTimeout(() => setMessage({ type: null, text: '' }), 3000);
  };

  const closeLinkModal = () => setLinkModalRow(null);

  const handleToggleCheckoutGps = async (row, checked) => {
    const confirmMsg = checked
      ? 'บังคับให้ต้องอยู่ในพื้นที่ GPS ของโครงการนี้ ถึงจะลงเวลาออกผ่าน Profile ได้?'
      : 'ปิดการบังคับเช็ค GPS ตอนลงเวลาออก (โครงการนี้)?';
    if (!window.confirm(confirmMsg)) return;
    try {
      await dispatch(
        actions.projectPut(row.id || row._id, {
          checkout_gps_required: checked,
        }),
      );

      // Update local state แทนการ reload
      const updatedProjects = projects.map(p =>
        (p.id || p._id) === (row.id || row._id)
          ? { ...p, checkout_gps_required: checked }
          : p
      );
      setProjects(updatedProjects);

      setMessage({ type: 'success', text: 'อัปเดตการบังคับ GPS ตอนออกแล้ว' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'อัปเดตไม่สำเร็จ' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    }
  };

  // เปิด/ปิด GPS ตอนออก ให้ "ทุกโครงการที่เปิด Profile" ทีเดียว (ไม่จำกัดแค่หน้าที่กำลังดูอยู่)
  const handleBulkToggleCheckoutGps = async (checked) => {
    const confirmMsg = checked
      ? 'เปิดบังคับ GPS ตอนลงเวลาออก ให้ "ทุกโครงการที่เปิด Profile" เลยไหม?'
      : 'ปิดบังคับ GPS ตอนลงเวลาออก ให้ "ทุกโครงการที่เปิด Profile" เลยไหม?';
    if (!window.confirm(confirmMsg)) return;
    setBulkGpsSaving(true);
    try {
      const result = await dispatch(actions.projectBulkSetCheckoutGps(checked));

      // Update local state (เฉพาะแถวที่โหลดอยู่ในหน้านี้) แทนการ reload
      const updatedProjects = projects.map((p) =>
        p.time_tracking_enabled ? { ...p, checkout_gps_required: checked } : p,
      );
      setProjects(updatedProjects);

      setMessage({
        type: 'success',
        text: `${checked ? 'เปิด' : 'ปิด'} GPS ตอนออกให้ ${result?.modifiedCount ?? 0} โครงการแล้ว`,
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'อัปเดตไม่สำเร็จ' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } finally {
      setBulkGpsSaving(false);
    }
  };

  const openEditModal = (row) => {
    setEditingProject(row);
    setEditLocation(row.location || '');
    const sites = [];
    if (row.gps?.lat != null && row.gps?.lon != null) {
      sites.push({
        id: 'primary',
        name: 'จุดหลัก',
        lat: String(row.gps.lat),
        lon: String(row.gps.lon),
      });
    }
    (row.gps_sites || []).forEach((s, i) => {
      if (s?.lat != null && s?.lon != null) {
        sites.push({
          id: `site-${i}`,
          name: s.name || `ไซต์ ${i + 1}`,
          lat: String(s.lat),
          lon: String(s.lon),
        });
      }
    });
    if (sites.length === 0) {
      sites.push({ id: 'new', name: '', lat: '', lon: '' });
    }
    setEditSites(sites);
    setActiveSiteIndex(0);
    const first = sites[0];
    const lat = first.lat?.trim();
    const lon = first.lon?.trim();
    setMapCenter(
      lat && lon && !Number.isNaN(parseFloat(lat)) && !Number.isNaN(parseFloat(lon))
        ? { lat: parseFloat(lat), lng: parseFloat(lon) }
        : DEFAULT_CENTER,
    );
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingProject(null);
    setEditLocation('');
    setEditSites([]);
    setActiveSiteIndex(0);
    setMapCenter(DEFAULT_CENTER);
  };

  const handleSaveLocation = async () => {
    if (!editingProject) return;
    const id = editingProject.id || editingProject._id;
    const valid = editSites
      .map((s) => ({
        name: (s.name || '').trim(),
        lat: s.lat?.trim() ? parseFloat(s.lat, 10) : NaN,
        lon: s.lon?.trim() ? parseFloat(s.lon, 10) : NaN,
      }))
      .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon));

    const payload = {
      location: editLocation.trim() || undefined,
      gps:
        valid.length > 0
          ? { lat: valid[0].lat, lon: valid[0].lon }
          : undefined,
      gps_sites: valid.slice(1).map((s) => ({
        name: s.name,
        lat: s.lat,
        lon: s.lon,
      })),
    };
    if (!payload.location && !payload.gps) {
      setMessage({
        type: 'warning',
        text: 'กรุณากรอกสถานที่หรือพิกัด GPS อย่างน้อย 1 จุด',
      });
      return;
    }
    setSaving(true);
    try {
      await dispatch(actions.projectPut(id, payload));
      
      // Update local state แทนการ reload
      const updatedProjects = projects.map(p => 
        (p.id || p._id) === id 
          ? { ...p, location: payload.location, gps: payload.gps, gps_sites: payload.gps_sites } 
          : p
      );
      setProjects(updatedProjects);
      
      setMessage({ type: 'success', text: 'บันทึกโลเคชั่นแล้ว' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
      closeEditModal();
    } catch (err) {
      setMessage({ type: 'error', text: 'บันทึกไม่สำเร็จ' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const gpsText = (row) => {
    const n = countProjectGpsPoints(row);
    if (n === 0) return '-';
    const pts = getProjectGpsPoints(row);
    const p0 = pts[0];
    return `${n} จุด · ${p0.lat.toFixed(5)}, ${p0.lon.toFixed(5)}`;
  };

  const renderSkeletonTable = () => (
    <Fade in={true} timeout={300}>
      <Card elevation={1} sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={150} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={120} animation="wave" /></TableCell>
                <TableCell><Skeleton variant="text" width={180} animation="wave" /></TableCell>
                <TableCell align="center"><Skeleton variant="text" width={60} animation="wave" /></TableCell>
                <TableCell align="center"><Skeleton variant="text" width={60} animation="wave" /></TableCell>
                <TableCell align="center"><Skeleton variant="text" width={60} animation="wave" /></TableCell>
                <TableCell align="center"><Skeleton variant="text" width={60} animation="wave" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
                <TableRow key={row}>
                  <TableCell><Skeleton variant="text" width={80} animation="wave" /></TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={200} animation="wave" />
                    <Skeleton variant="text" width={150} height={12} animation="wave" />
                  </TableCell>
                  <TableCell><Skeleton variant="text" width={100} animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" width={160} animation="wave" /></TableCell>
                  <TableCell align="center"><Skeleton variant="rounded" width={40} height={24} animation="wave" /></TableCell>
                  <TableCell align="center"><Skeleton variant="rounded" width={40} height={24} animation="wave" /></TableCell>
                  <TableCell align="center"><Skeleton variant="rounded" width={40} height={24} animation="wave" /></TableCell>
                  <TableCell align="center"><Skeleton variant="circular" width={32} height={32} animation="wave" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Skeleton variant="rounded" width={200} height={36} animation="wave" />
        </Box>
      </Card>
    </Fade>
  );

  if (loading) {
    return (
      <Box>
        <Box mb={2}>
          <Skeleton variant="text" width={400} height={24} animation="wave" />
        </Box>
        {renderSkeletonTable()}
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          เปิด/ปิดการลงเวลา (Profile / ลิงก์) แต่ละโครงการ และจัดการโลเคชั่น
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          bgcolor: alpha(theme.palette.warning.main, 0.04),
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={600}>
            บังคับ GPS ตอนลงเวลาออก — ทุกโครงการที่เปิด Profile
          </Typography>
          <Typography variant="caption" color="text.secondary">
            เปิด/ปิดคอลัมน์ "GPS ตอนออก" ให้ทุกโครงการที่เปิด "Profile" อยู่ ทีเดียว
            (ไม่กระทบโครงการที่ปิด Profile)
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={allProfileProjectsHaveGps}
              onChange={(e) => handleBulkToggleCheckoutGps(e.target.checked)}
              disabled={bulkGpsSaving}
              color="warning"
            />
          }
          label={bulkGpsSaving ? 'กำลังบันทึก...' : 'เปิดทั้งหมด'}
        />
      </Paper>

      {message.text && (
        <Alert
          severity={message.type || 'info'}
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: null, text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>เลขที่งาน</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>โครงการ</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>สถานที่ (Location)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                GPS (จำนวนจุด / พิกัดแรก)
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Profile
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                ลิงก์
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                <Tooltip title="บังคับให้ต้องอยู่ในพื้นที่ GPS ของโครงการที่เช็คอินไว้ ถึงจะลงเวลาออกผ่าน Profile ได้ (ใช้ได้เฉพาะโครงการที่เปิด Profile)">
                  <span>GPS ตอนออก</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                จัดการ
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  ไม่มีโครงการ
                </TableCell>
              </TableRow>
            ) : (
              projects.map((row) => (
                <TableRow key={row.id || row._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {row.project_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.name || '-'}
                    </Typography>
                    {row.customer && (
                      <Typography variant="caption" color="text.secondary">
                        {row.customer}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.location ? (
                      <Typography variant="body2">{row.location}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {gpsText(row)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={Boolean(row.time_tracking_enabled)}
                      onChange={(e) =>
                        handleToggleTimeTracking(row, e.target.checked)
                      }
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={row.time_tracking_link_enabled !== false}
                      onChange={(e) =>
                        handleToggleTimeTrackingLink(row, e.target.checked)
                      }
                      color="secondary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        row.time_tracking_enabled
                          ? ''
                          : 'ต้องเปิด "Profile" ก่อน ถึงจะตั้งค่านี้ได้'
                      }
                    >
                      <span>
                        <Switch
                          checked={Boolean(row.checkout_gps_required)}
                          disabled={!row.time_tracking_enabled}
                          onChange={(e) =>
                            handleToggleCheckoutGps(row, e.target.checked)
                          }
                          color="warning"
                          size="small"
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {row.time_tracking_link_enabled !== false && (
                        <Button
                          size="small"
                          color="secondary"
                          variant="outlined"
                          startIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={() => setLinkModalRow(row)}
                        >
                          เปิดลิงก์
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => openEditModal(row)}
                      >
                        แก้ไข
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} จาก ${count}`
          }
        />
      </TableContainer>

      <Modal open={editModalOpen} onClose={closeEditModal}>
        <Box sx={modalStyle}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">
              <LocationOnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              แก้ไขโลเคชั่น
            </Typography>
            <IconButton size="small" onClick={closeEditModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          {editingProject && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              เลขที่งาน: {editingProject.project_number || '-'} · โครงการ: {editingProject.name}
            </Typography>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            เลือกจุดที่ต้องการ แล้วคลิกบนแผนที่เพื่อใส่พิกัด (แถวแรก = จุดหลัก gps)
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="active-site-label">จุดที่แก้จากแผนที่</InputLabel>
            <Select
              labelId="active-site-label"
              label="จุดที่แก้จากแผนที่"
              value={Math.min(activeSiteIndex, Math.max(0, editSites.length - 1))}
              onChange={(e) => setActiveSiteIndex(Number(e.target.value))}
            >
              {editSites.map((s, i) => (
                <MenuItem key={s.id || i} value={i}>
                  {s.name?.trim() || `จุด ${i + 1}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              height: 220,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Suspense
              fallback={
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  minHeight={220}
                >
                  <CircularProgress size={32} />
                </Box>
              }
            >
              <HrCheckinManagementMap
                center={mapCenter}
                zoom={DEFAULT_ZOOM}
                apiKey={process.env.REACT_APP_MAP_API_KEY}
                onClick={({ lat, lng }) => {
                  const next = [...editSites];
                  const i = Math.min(
                    activeSiteIndex,
                    Math.max(0, next.length - 1),
                  );
                  if (!next[i]) return;
                  next[i] = {
                    ...next[i],
                    lat: lat.toFixed(6),
                    lon: lng.toFixed(6),
                  };
                  setEditSites(next);
                  setMapCenter({ lat, lng });
                }}
              >
                {editSites.map((s, i) => {
                  const la = s.lat?.trim();
                  const lo = s.lon?.trim();
                  if (
                    !la ||
                    !lo ||
                    Number.isNaN(parseFloat(la)) ||
                    Number.isNaN(parseFloat(lo))
                  ) {
                    return null;
                  }
                  return (
                    <MapMarker
                      key={s.id || i}
                      lat={parseFloat(la)}
                      lng={parseFloat(lo)}
                    />
                  );
                })}
              </HrCheckinManagementMap>
            </Suspense>
          </Box>

          <TextField
            fullWidth
            label="สถานที่ (Location)"
            value={editLocation}
            onChange={(e) => setEditLocation(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
            placeholder="ชื่อสถานที่หรือที่อยู่"
          />

          {editSites.map((s, i) => (
            <Box
              key={s.id || i}
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor:
                  i === activeSiteIndex ? 'primary.main' : 'divider',
                bgcolor:
                  i === activeSiteIndex ? 'action.hover' : 'transparent',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {i === 0 ? 'จุดหลัก (gps)' : `ไซต์เพิ่ม #${i}`}
              </Typography>
              <TextField
                fullWidth
                label="ชื่อไซต์"
                value={s.name}
                onChange={(e) => {
                  const next = [...editSites];
                  next[i] = { ...next[i], name: e.target.value };
                  setEditSites(next);
                }}
                size="small"
                sx={{ mt: 1, mb: 1 }}
              />
              <TextField
                fullWidth
                label="ละติจูด (Lat)"
                value={s.lat}
                onChange={(e) => {
                  const next = [...editSites];
                  next[i] = { ...next[i], lat: e.target.value };
                  setEditSites(next);
                }}
                size="small"
                type="number"
                inputProps={{ step: 0.00001 }}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                label="ลองจิจูด (Lon)"
                value={s.lon}
                onChange={(e) => {
                  const next = [...editSites];
                  next[i] = { ...next[i], lon: e.target.value };
                  setEditSites(next);
                }}
                size="small"
                type="number"
                inputProps={{ step: 0.00001 }}
              />
            </Box>
          ))}

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddLocationAltIcon />}
            onClick={() => {
              const n = editSites.length;
              setEditSites([
                ...editSites,
                {
                  id: `new-${Date.now()}`,
                  name: `ไซต์ ${n + 1}`,
                  lat: '',
                  lon: '',
                },
              ]);
              setActiveSiteIndex(n);
            }}
            sx={{ mb: 2 }}
          >
            เพิ่มไซต์
          </Button>
          <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <Button variant="outlined" onClick={closeEditModal} size="small">
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSaveLocation}
              disabled={saving}
              size="small"
            >
              บันทึก
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={!!linkModalRow} onClose={closeLinkModal}>
        <Box sx={modalStyle}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">
              <OpenInNewIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              ลิงก์ลงเวลา
            </Typography>
            <IconButton size="small" onClick={closeLinkModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          {linkModalRow && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                เลขที่งาน: {linkModalRow.project_number || '-'} · โครงการ: {linkModalRow.name || '-'}
                {linkModalRow.customer && ` · ลูกค้า: ${linkModalRow.customer}`}
                {linkModalRow.location && ` · สถานที่: ${linkModalRow.location}`}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                ลิงก์สำหรับลงเวลา
              </Typography>
              <Box sx={{ mb: 2 }}>
                <TextField
                  value={getTimeTrackingLink(linkModalRow)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => handleCopyLink(linkModalRow)}
                          size="small"
                          color="primary"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Button
                variant="contained"
                onClick={() => handleCopyLink(linkModalRow)}
                size="small"
                fullWidth
                startIcon={<ContentCopyIcon />}
              >
                คัดลอกลิงก์
              </Button>
            </>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={closeLinkModal} size="small">
              ปิด
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
